// src/utils/abbonamenti.js
// ═══════════════════════════════════════════════════════════════════════════
// ABBONAMENTI: RICHIESTA DEL CLIENTE, PROFORMA E ATTIVAZIONE (ottobre 2026, W431, regole 70)
// ═══════════════════════════════════════════════════════════════════════════
// Decisioni di Paolo (24/10/2026):
//  • l'abbonamento si paga UNA VOLTA L'ANNO, in anticipo, con lo sconto annuale del listino
//    (niente bonifico mensile); prezzi = utils/pianiNexiora.js (stessi del sito);
//  • niente offerta: il prezzo è quello pubblico. Il Platform Owner manda una PROFORMA
//    (richiesta di pagamento, non valida ai fini fiscali) con IBAN e scadenza;
//  • la fattura elettronica resta fuori da Nexiora (dal programma di Paolo): in Nexiora si
//    annota solo il numero;
//  • pagata la proforma, il piano si attiva da solo (piano, posti, periodo);
//  • posti aggiunti durante l'anno: proforma di integrazione in proporzione ai giorni residui.
// Solo calcoli: nessun accesso al database (prova demo/prove/prova_abbonamenti.mjs).
// ═══════════════════════════════════════════════════════════════════════════
import { LISTINO, PIANI_INFO, canoneAnnuale, canoneMensile, normalizzaPiano, postiDaPagare } from "./pianiNexiora";

export const IVA_ABBONAMENTO = 22;
export const GIORNI_PAGAMENTO_PROFORMA = 15;
export const GIORNI_AVVISO_RINNOVO = 30;
const arr2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const giornoUtc = (s) => { const [a, m, g] = String(s).split("-").map(Number); return Date.UTC(a, m - 1, g); };
const ds = (ms) => new Date(ms).toISOString().slice(0, 10);
export const piuGiorni = (s, n) => ds(giornoUtc(s) + n * 86400000);
/** Fine del periodo annuale: dal 01/11/2026 → al 31/10/2027 */
export function fineAnno(dal) {
  const [a, m, g] = String(dal).split("-").map(Number);
  return ds(Date.UTC(a + 1, m - 1, g) - 86400000);
}
const giorniTra = (dal, al) => Math.round((giornoUtc(al) - giornoUtc(dal)) / 86400000) + 1;
export const dataIt = (s) => { const [a, m, g] = String(s || "").split("-"); return a && m && g ? `${g}/${m}/${a}` : ""; };
export const numeroProforma = (anno, n) => `NXA-${anno}-${String(n).padStart(4, "0")}`;

/**
 * Calcolo della proforma.
 * @param p { tipo: "attivazione" | "rinnovo" | "integrazione", piano, posti, dal, al?, postiPrima? }
 *   per l'integrazione: posti = nuovi posti totali, postiPrima = posti già pagati, dal = oggi, al = fine del periodo pagato
 * @returns { voci, imponibile, iva, totale, dal, al, posti, piano, spiegazione: [testo] }
 */
export function calcolaProforma(p = {}) {
  const piano = normalizzaPiano(p.piano) === "gold" ? "gold" : "pro";
  const posti = postiDaPagare(Number(p.posti) || 0);
  const nome = PIANI_INFO[piano].label;
  const dal = p.dal;
  const voci = [], spiegazione = [];
  let al = p.al || (dal ? fineAnno(dal) : "");
  if (p.tipo === "integrazione") {
    const prima = postiDaPagare(Number(p.postiPrima) || 0);
    const diffAnno = arr2(canoneAnnuale(posti, piano) - canoneAnnuale(prima, piano));
    const giorni = dal && al ? Math.max(0, giorniTra(dal, al)) : 0;
    const importo = arr2(diffAnno * giorni / 365);
    voci.push({ descrizione: `Nexiora ${nome}: da ${prima} a ${posti} posti, dal ${dataIt(dal)} al ${dataIt(al)}`, dettaglio: `Differenza annuale ${euroAb(diffAnno)} × ${giorni} giorni / 365`, importo });
    spiegazione.push(`I posti aggiunti si pagano solo per i giorni che restano fino alla fine del periodo già pagato (${giorni} giorni).`);
  } else {
    const mensile = canoneMensile(posti, piano), annuo = canoneAnnuale(posti, piano);
    voci.push({ descrizione: `Abbonamento Nexiora ${nome}, ${posti} posti, dal ${dataIt(dal)} al ${dataIt(al)}`,
      dettaglio: `${euroAb(mensile)} al mese × 12 mesi, con lo sconto annuale del ${Math.round(LISTINO.scontoAnnuale * 100)}%`, importo: annuo });
    spiegazione.push(`Prezzo di listino: ${euroAb(mensile)} al mese per ${posti} posti; pagando l'anno in anticipo si applica lo sconto del ${Math.round(LISTINO.scontoAnnuale * 100)}%.`);
  }
  const imponibile = arr2(voci.reduce((t, v) => t + v.importo, 0));
  const iva = arr2(imponibile * IVA_ABBONAMENTO / 100);
  return { voci, imponibile, iva, totale: arr2(imponibile + iva), dal, al, posti, piano, spiegazione, ivaPerc: IVA_ABBONAMENTO };
}
export const euroAb = (n) => "€ " + arr2(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Aggiornamento dell'organizzazione quando la proforma è pagata (attiva o rinnova il piano). */
export function attivazioneDaProforma(pf, org = {}) {
  if (pf.tipo === "integrazione") return { seats: pf.posti, pianoPagatoFino: org.pianoPagatoFino || pf.al };
  return { plan: pf.piano, status: "active", seats: pf.posti, billingCycle: "annual", pianoPagatoDal: pf.dal, pianoPagatoFino: pf.al };
}

/** Situazione del rinnovo: { giorni, inScadenza, scaduto } rispetto a oggi ("AAAA-MM-GG"). */
export function statoRinnovo(org = {}, oggi) {
  if (!org.pianoPagatoFino || !oggi) return { giorni: null, inScadenza: false, scaduto: false };
  const giorni = Math.round((giornoUtc(org.pianoPagatoFino) - giornoUtc(oggi)) / 86400000);
  return { giorni, inScadenza: giorni >= 0 && giorni <= GIORNI_AVVISO_RINNOVO, scaduto: giorni < 0 };
}
